import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { S, StatusBadge, formatCHF, formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// D-28: no invoice detail view existed at all, so clicking a row on the
// Invoices tab did nothing. Anjani reported "Can not open an invoice
// detail". Right-side sliding panel matches the OfferDetailPanel /
// SelectedBroker pattern used elsewhere. Loads by _id via GET
// /invoices/:id so the panel refreshes if the tester opens a row
// straight after a status change.
//
// Q-08: bundle a Print action so testers can produce a hard copy.
// Uses window.print with a print-scoped stylesheet limited to this
// panel — no server-side PDF template exists yet and the acceptance
// test only asks that download/print "is available".
const InvoiceDetailPanel = ({ invoiceId, onClose }) => {
  const { t } = useTranslation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${BASE_URL}/invoices/${invoiceId}`, { withCredentials: true })
      .then(resp => {
        if (cancelled) return;
        const raw = resp.data?.data?.data || resp.data?.data || null;
        setInvoice(raw);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || t('screens.invoicing.loadFailed', { defaultValue: 'Failed to load invoice' }));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [invoiceId, t]);

  const Row = ({ label, value, mono, valueStyle }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '11px 0', borderBottom: `1px solid ${S.borderLight}`, gap: 12 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary, fontFamily: mono ? 'monospace' : undefined, textAlign: 'right', ...(valueStyle || {}) }}>{value || '—'}</span>
    </div>
  );

  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.35)', zIndex: 90 }}
      onClick={onClose}
    >
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #invoice-print-panel, #invoice-print-panel * { visibility: visible !important; }
          #invoice-print-panel { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; box-shadow: none !important; }
          #invoice-print-panel .no-print { display: none !important; }
        }
      `}</style>
      <div
        id='invoice-print-panel'
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          background: '#fff',
          boxShadow: '-10px 0 30px rgba(0,0,0,0.12)',
          padding: '28px 32px',
          overflowY: 'auto',
        }}
      >
        <div className='no-print' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('screens.invoicing.detailTitle', { defaultValue: 'Invoice Details' })}</div>
          <button type='button' onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: S.textMuted, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: S.textMuted, fontSize: 13 }}>
            {t('screens.invoicing.loading', { defaultValue: 'Loading…' })}
          </div>
        )}

        {error && (
          <div style={{ padding: 12, marginBottom: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{error}</div>
        )}

        {!loading && invoice && (
          <>
            <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{invoice.invoiceNumber || invoice._id}</div>
            <div style={{ marginBottom: 18 }}>
              <StatusBadge status={invoice.status} />
            </div>

            <Row label={t('screens.invoicing.colClient')} value={invoice.client} />
            <Row label={t('screens.invoicing.colPolicy')} value={invoice.policyNumber || '—'} mono />
            <Row
              label={t('screens.invoicing.colAmount')}
              value={`${invoice.currency ? invoice.currency + ' ' : ''}${formatCHF(invoice.amount)}`}
              valueStyle={{ fontSize: 16, color: S.burgundy }}
            />
            <Row label={t('screens.invoicing.colDueDate')} value={formatDate(invoice.dueDate)} />
            <Row label={t('screens.invoicing.paidOn', { defaultValue: 'Paid on' })} value={invoice.paidDate ? formatDate(invoice.paidDate) : '—'} />
            <Row label={t('screens.invoicing.colProduct')} value={invoice.product || invoice.lineOfBusiness || '—'} />
            <Row label={t('screens.invoicing.created', { defaultValue: 'Created' })} value={invoice.createdAt ? formatDate(invoice.createdAt) : '—'} />

            {invoice.notes && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
                  {t('screens.invoicing.notes', { defaultValue: 'Notes' })}
                </div>
                <div style={{ fontSize: 13, color: S.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{invoice.notes}</div>
              </div>
            )}

            {Array.isArray(invoice.lineItems) && invoice.lineItems.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
                  {t('screens.invoicing.lineItems', { defaultValue: 'Line items' })}
                </div>
                {invoice.lineItems.map((li, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${S.borderLight}`, fontSize: 13 }}>
                    <span style={{ color: S.textSecondary }}>{li.label || li.description || `#${i + 1}`}</span>
                    <span style={{ color: S.textPrimary, fontWeight: 600 }}>{formatCHF(li.amount || 0)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className='no-print' style={{ marginTop: 24, display: 'flex', gap: 10 }}>
              <button
                type='button'
                onClick={() => window.print()}
                style={{ flex: 1, padding: '10px 14px', background: S.burgundy, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('screens.invoicing.actionPrint', { defaultValue: 'Print' })}
              </button>
              <button
                type='button'
                onClick={onClose}
                style={{ flex: 1, padding: '10px 14px', background: '#fff', color: S.textSecondary, border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('screens.invoicing.actionClose', { defaultValue: 'Close' })}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailPanel;
